import { ArrowRight, ChevronDown, CircleHelp, Phone, Sparkles } from "lucide-react";
import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { getAttributionSource, trackEvent } from "../analytics";
import { AdvisorCard } from "../AdvisorCard";
import {
  COMPANY,
  CONTACT_CONSENT_TEXT,
  formatPhoneInput,
  hasMinimumPhoneDigits,
  PRIVACY_CONSENT_TEXT,
  PRIVACY_VERSION,
  submitLead
} from "../leadCapture";
import type { CalcInputs, CalcResult, Field, ProductConfig } from "./types";

export type LeadQuoteSummary = { title: string; amount: string; details: string[] };
export type LeadContext = { product: string; message: string; summary: LeadQuoteSummary };
type QuickLeadState = {
  name: string;
  phone: string;
  contactConsent: boolean;
  privacyConsent: boolean;
  website: string;
};

const initialQuickLead: QuickLeadState = {
  name: "",
  phone: "",
  contactConsent: false,
  privacyConsent: false,
  website: ""
};

function defaultInputs(fields: Field[]): CalcInputs {
  const out: CalcInputs = {};
  for (const field of fields) {
    out[field.id] = field.default;
  }
  return out;
}

function formatSlider(field: Extract<Field, { type: "slider" }>, value: number): string {
  if (field.format === "zl") return `${value.toLocaleString("pl-PL")} zł`;
  if (field.format === "lata") return `${value} lat`;
  return String(value);
}

function amountText(result: CalcResult) {
  return `${Math.round(result.amount).toLocaleString("pl-PL")} ${result.amountSuffix}`;
}

function fieldValueLabel(field: Field, value: number | string | boolean) {
  if (field.type === "slider") return formatSlider(field, Number(value));
  if (field.type === "toggle") return value === true ? "Tak" : "Nie";
  return field.options.find((option) => option.value === value)?.label ?? String(value);
}

function buildLeadContext(config: ProductConfig, inputs: CalcInputs, result: CalcResult): LeadContext {
  const fieldDetails = config.fields.map((field) => `${field.label}: ${fieldValueLabel(field, inputs[field.id])}`);
  const breakdown = result.breakdown?.map((row) => `${row.label}: ${row.value}`) ?? [];
  const details = [...fieldDetails, ...breakdown];
  const message = [
    `Kalkulator: ${config.cardTitle}`,
    `Wynik: ${amountText(result)}`,
    result.note ? `Notatka: ${result.note}` : "",
    "Parametry:",
    ...details.map((detail) => `- ${detail}`)
  ]
    .filter(Boolean)
    .join("\n");

  return {
    product: config.leadProduct,
    message,
    summary: {
      title: config.cardTitle,
      amount: amountText(result),
      details
    }
  };
}

export function ProductCalculator({
  config,
  headingLevel = "h2",
  isPrimary = false,
  className = ""
}: {
  config: ProductConfig;
  headingLevel?: "h1" | "h2";
  isPrimary?: boolean;
  className?: string;
}) {
  const [inputs, setInputs] = useState<CalcInputs>(() => defaultInputs(config.fields));
  const [revealed, setRevealed] = useState(false);
  const [quickLead, setQuickLead] = useState<QuickLeadState>(initialQuickLead);
  const [quickStatus, setQuickStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [quickMessage, setQuickMessage] = useState("");
  const [quickConsentError, setQuickConsentError] = useState("");
  const resultRef = useRef<HTMLDivElement | null>(null);
  const quickStartedRef = useRef(false);
  const quickConsentRef = useRef<HTMLInputElement | null>(null);
  const quickFormRef = useRef<HTMLFormElement | null>(null);
  const quickPhoneRef = useRef<HTMLInputElement | null>(null);
  const quickSubmitRef = useRef<HTMLButtonElement | null>(null);
  const [stickyVisible, setStickyVisible] = useState(false);

  const result = useMemo(() => config.compute(inputs), [config, inputs]);
  const leadContext = useMemo(() => buildLeadContext(config, inputs, result), [config, inputs, result]);
  const HeadingTag = headingLevel;

  const set = (id: string, value: number | string | boolean) =>
    setInputs((current) => ({ ...current, [id]: value }));

  const markQuickFormStarted = () => {
    if (quickStartedRef.current) return;
    quickStartedRef.current = true;
    trackEvent("formularz_start", {
      product: config.leadProduct,
      source: "calculator_quick_form"
    });
  };

  const revealResult = () => {
    setRevealed(true);
    trackEvent("oblicz_skladke", {
      product: config.id,
      amount: Math.round(result.amount)
    });
    window.requestAnimationFrame(() => {
      const panel = resultRef.current;
      if (!panel) return;
      // "start" zamiast "center": na mobile wynik i pole telefonu mieszczą się razem na ekranie.
      const from = window.scrollY;
      panel.scrollIntoView({ block: "start", behavior: "smooth" });
      // Gdy silnik nie animuje smooth scrolla, dociągnij natychmiast — wynik musi wejść w kadr.
      window.setTimeout(() => {
        if (Math.abs(window.scrollY - from) < 4) {
          panel.scrollIntoView({ block: "start", behavior: "auto" });
        }
      }, 400);
    });
  };

  // Sticky pasek CTA (mobile): widoczny zawsze, gdy właściwy przycisk wysyłki jest poza
  // ekranem — po kalkulacji użytkownik ma pod kciukiem kontakt, nie musi go szukać.
  useEffect(() => {
    if (!revealed || typeof IntersectionObserver === "undefined") return;
    const submit = quickSubmitRef.current;
    if (!submit) return;
    const observer = new IntersectionObserver(([entry]) => setStickyVisible(!entry.isIntersecting), {
      threshold: 1
    });
    observer.observe(submit);
    return () => observer.disconnect();
  }, [revealed]);

  // Powiadom widget czatu, że układ dołu ekranu się zmienił (launcher unosi się nad paskiem).
  useEffect(() => {
    window.dispatchEvent(new Event("ochro:layout"));
  }, [stickyVisible]);

  const updateQuickLead = <Key extends keyof QuickLeadState>(key: Key, value: QuickLeadState[Key]) => {
    markQuickFormStarted();
    setQuickLead((current) => ({ ...current, [key]: value }));
    if (key === "contactConsent" || key === "privacyConsent") {
      setQuickConsentError("");
    }
  };

  const updateRequiredQuickConsent = (checked: boolean) => {
    markQuickFormStarted();
    setQuickLead((current) => ({
      ...current,
      contactConsent: checked,
      privacyConsent: checked
    }));
    if (checked) setQuickConsentError("");
  };

  const submitQuickLead = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    markQuickFormStarted();
    setQuickMessage("");

    if (!hasMinimumPhoneDigits(quickLead.phone)) {
      setQuickStatus("error");
      setQuickMessage("Podaj numer telefonu z co najmniej 9 cyframi.");
      return;
    }

    if (!quickLead.contactConsent || !quickLead.privacyConsent) {
      setQuickStatus("idle");
      setQuickConsentError("Zaznacz zgodę na kontakt i potwierdzenie informacji o przetwarzaniu danych.");
      quickConsentRef.current?.focus({ preventScroll: true });
      return;
    }

    setQuickConsentError("");
    setQuickStatus("submitting");

    try {
      await submitLead({
        name: quickLead.name,
        phone: quickLead.phone,
        product: config.leadProduct,
        message: leadContext.message,
        contactConsent: quickLead.contactConsent,
        privacyConsent: quickLead.privacyConsent,
        marketingConsent: false,
        website: quickLead.website,
        source: getAttributionSource(`${window.location.pathname}#kalkulator`),
        recommendation: config.cardTitle,
        legalVersion: PRIVACY_VERSION,
        contactConsentText: CONTACT_CONSENT_TEXT,
        privacyConsentText: PRIVACY_CONSENT_TEXT,
        marketingConsentText: ""
      });

      setQuickStatus("success");
      setQuickMessage("Dzięki! Zgłoszenie zapisane — Martyna oddzwoni zwykle w ciągu 30 minut (pn–pt 9–18).");
      trackEvent("lead_success", { product: config.leadProduct, source: "calculator_quick_form" });
      setQuickLead(initialQuickLead);
      quickStartedRef.current = false;
    } catch {
      setQuickStatus("error");
      setQuickMessage("Nie udało się wysłać formularza. Spróbuj ponownie za chwilę albo zadzwoń:");
      trackEvent("lead_error", { product: config.leadProduct, source: "calculator_quick_form" });
    }
  };

  const details = config.toolBullets.length ? (
    <div className="tool-details">
      <ul className="clean-list">
        {config.toolBullets.map((bullet) => (
          <li key={bullet}>{bullet}</li>
        ))}
      </ul>
    </div>
  ) : null;

  return (
    <section className={`tool-band${isPrimary ? " tool-band-primary" : ""}${className ? ` ${className}` : ""}`} id="kalkulator" data-product={config.id}>
      <div className="tool-copy">
        <p className="section-kicker">Kalkulator</p>
        <HeadingTag>{config.toolTitle}</HeadingTag>
        <p>{config.toolIntro}</p>
        {isPrimary && <CalculatorAssurance className="tool-assurance-desktop" />}
        {!isPrimary && details}
      </div>

      <div className="calculator-shell" aria-label={`Kalkulator: ${config.cardTitle}`}>
        {config.fields.map((field) => (
          <FieldControl key={field.id} field={field} value={inputs[field.id]} onChange={(v) => set(field.id, v)} />
        ))}

        <button className="calc-button" type="button" onClick={revealResult}>
          Oblicz składkę
          <ArrowRight size={18} aria-hidden="true" />
        </button>

        {revealed ? (
          <>
            <div ref={resultRef} className="result-panel" role="status" aria-live="polite">
              <Sparkles size={22} aria-hidden="true" />
              <div className="result-panel-body">
                <span className="result-label">{result.amountLabel}</span>
                <strong>
                  {Math.round(result.amount).toLocaleString("pl-PL")} {result.amountSuffix}
                </strong>
                {result.rangeLow && result.rangeHigh ? (
                  <span className="result-range">
                    Zwykle {Math.round(result.rangeLow).toLocaleString("pl-PL")}–
                    {Math.round(result.rangeHigh).toLocaleString("pl-PL")} {result.amountSuffix} — dokładną kwotę
                    potwierdza doradca.
                  </span>
                ) : null}
                <details className="result-details">
                  <summary>Szczegóły kalkulacji</summary>
                  <p>{result.note}</p>
                  {result.breakdown && result.breakdown.length > 0 && (
                    <dl className="result-breakdown">
                      {result.breakdown.map((row) => (
                        <div key={row.label}>
                          <dt>{row.label}</dt>
                          <dd>{row.value}</dd>
                        </div>
                      ))}
                    </dl>
                  )}
                </details>
              </div>
            </div>
            <form ref={quickFormRef} className="quick-lead-form" onSubmit={submitQuickLead} onFocusCapture={markQuickFormStarted}>
              <div className="quick-lead-head">
                <span>Bez zobowiązań</span>
                <strong>Otrzymaj 3 warianty.</strong>
                <p>Doradca dostanie Twój wynik i porówna wariant tańszy, optymalny oraz szerszy.</p>
              </div>
              <AdvisorCard variant="compact" />

              <div className="quick-lead-grid">
                <label aria-label="Telefon">
                  <input
                    ref={quickPhoneRef}
                    required
                    type="tel"
                    aria-label="Telefon"
                    autoComplete="tel"
                    inputMode="tel"
                    pattern="(?:[^0-9]*[0-9]){9,}[^0-9]*"
                    title="Podaj numer telefonu z co najmniej 9 cyframi."
                    value={quickLead.phone}
                    onChange={(event) => updateQuickLead("phone", formatPhoneInput(event.target.value))}
                    placeholder="Telefon"
                  />
                </label>
                <label aria-label="Imię">
                  <input
                    aria-label="Imię"
                    autoComplete="given-name"
                    value={quickLead.name}
                    onChange={(event) => updateQuickLead("name", event.target.value)}
                    placeholder="Imię (opcjonalnie)"
                  />
                </label>
              </div>

              <label className="honeypot" aria-hidden="true">
                Strona internetowa
                <input
                  tabIndex={-1}
                  autoComplete="off"
                  value={quickLead.website}
                  onChange={(event) => updateQuickLead("website", event.target.value)}
                />
              </label>

              <div className="quick-consent">
                <label className={quickConsentError ? "has-error" : undefined}>
                  <input
                    ref={quickConsentRef}
                    name="quickRequiredConsent"
                    type="checkbox"
                    checked={quickLead.contactConsent && quickLead.privacyConsent}
                    onChange={(event) => updateRequiredQuickConsent(event.target.checked)}
                    aria-invalid={Boolean(quickConsentError)}
                    aria-describedby={quickConsentError ? "quick-consent-error" : undefined}
                  />
                  <span>Zgadzam się na telefon w sprawie mojego zgłoszenia i znam informację o danych.</span>
                </label>
                {quickConsentError && (
                  <p className="consent-error" id="quick-consent-error" role="alert">
                    {quickConsentError}
                  </p>
                )}
                <details className="consent-details">
                  <summary>Szczegóły zgody i danych</summary>
                  <small>
                    {CONTACT_CONSENT_TEXT} {PRIVACY_CONSENT_TEXT} Szczegóły opisuje{" "}
                    <a href="/polityka-prywatnosci/" target="_blank" rel="noopener noreferrer">
                      polityka prywatności
                    </a>
                    .
                  </small>
                </details>
              </div>

              <button
                ref={quickSubmitRef}
                className="quick-lead-submit"
                type="submit"
                disabled={quickStatus === "submitting"}
              >
                {quickStatus === "submitting" ? "Wysyłanie..." : "Otrzymaj 3 warianty"}
                <ArrowRight size={18} aria-hidden="true" />
              </button>

              <a
                className="quick-lead-call"
                href={COMPANY.phoneHref}
                onClick={() => trackEvent("telefon_click", { source: "calc_result" })}
              >
                <Phone size={16} aria-hidden="true" />
                Wolisz porozmawiać od razu? Zadzwoń: {COMPANY.phoneDisplay}
              </a>

              {quickMessage && (
                <p className={`form-status ${quickStatus === "error" ? "is-error" : "is-success"}`} role="status">
                  {quickMessage}
                  {quickStatus === "error" && (
                    <>
                      {" "}
                      <a href={COMPANY.phoneHref} onClick={() => trackEvent("telefon_click", { source: "calc_error" })}>
                        {COMPANY.phoneDisplay}
                      </a>
                    </>
                  )}
                </p>
              )}
            </form>
          </>
        ) : null}
      </div>

      {isPrimary && <CalculatorAssurance className="tool-assurance-mobile" />}

      {revealed && stickyVisible && (
        <div className="calc-sticky-cta">
          <a
            className="calc-sticky-call"
            href={COMPANY.phoneHref}
            onClick={() => trackEvent("telefon_click", { source: "calc_sticky" })}
          >
            <Phone size={17} aria-hidden="true" />
            Zadzwoń
          </a>
          <button
            type="button"
            className="calc-sticky-form"
            onClick={() => {
              quickFormRef.current?.scrollIntoView({ block: "start", behavior: "smooth" });
              quickPhoneRef.current?.focus({ preventScroll: true });
            }}
          >
            Otrzymaj 3 warianty
            <ArrowRight size={17} aria-hidden="true" />
          </button>
        </div>
      )}
    </section>
  );
}

function FieldControl({
  field,
  value,
  onChange
}: {
  field: Field;
  value: number | string | boolean;
  onChange: (value: number | string | boolean) => void;
}) {
  if (field.type === "slider") {
    return (
      <div className={`calc-field calc-field-${field.id}`}>
        <div className="control-row">
          <FieldHeading label={field.label} htmlFor={field.id} help={field.help} />
          <output htmlFor={field.id}>{formatSlider(field, Number(value))}</output>
        </div>
        <input
          id={field.id}
          type="range"
          min={field.min}
          max={field.max}
          step={field.step}
          value={Number(value)}
          onChange={(event) => onChange(Number(event.target.value))}
        />
      </div>
    );
  }

  if (field.type === "toggle") {
    return (
      <div className={`calc-field calc-field-${field.id}`}>
        <div className="toggle-row">
          <label className="toggle-line">
            <input type="checkbox" checked={value === true} onChange={(event) => onChange(event.target.checked)} />
            <span>{field.label}</span>
          </label>
          <FieldHelp label={field.label} help={field.help} />
        </div>
      </div>
    );
  }

  if (field.type === "segmented") {
    const labelId = `${field.id}-label`;
    return (
      <div className={`calc-field calc-field-${field.id}`}>
        <FieldHeading id={labelId} label={field.label} help={field.help} />
        <div className="segmented" style={{ gridTemplateColumns: `repeat(${field.options.length}, minmax(0, 1fr))` }} role="group" aria-labelledby={labelId}>
          {field.options.map((option) => (
            <button
              key={option.value}
              type="button"
              className={value === option.value ? "is-selected" : ""}
              aria-pressed={value === option.value}
              onClick={() => onChange(option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>
    );
  }

  // select
  return (
    <div className={`calc-field calc-field-${field.id}`}>
      <div className="select-wrap">
        <FieldHeading label={field.label} htmlFor={field.id} help={field.help} />
        <select id={field.id} value={String(value)} onChange={(event) => onChange(event.target.value)}>
          {field.options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <ChevronDown size={18} aria-hidden="true" />
      </div>
    </div>
  );
}

function CalculatorAssurance({ className = "" }: { className?: string }) {
  const items = [
    {
      title: "Darmowa wycena",
      text: "Sprawdzasz orientacyjną składkę bez zobowiązań, a doradca pomaga doprecyzować wariant pod Twoją sytuację."
    },
    {
      title: "Zakres bez skrótów",
      text: "Porównujemy nie tylko cenę, ale też sumę ubezpieczenia, dodatki, karencje, wyłączenia i definicje z OWU."
    },
    {
      title: "Kontakt po kalkulacji",
      text: "Po sprawdzeniu wyniku możesz przejść do kontaktu i spokojnie omówić dokładniejszą propozycję z doradcą."
    },
    {
      title: "Decyzja po Twojej stronie",
      text: "Najpierw omawiamy potrzeby i budżet, a dopiero później wybierasz, czy chcesz przejść do wniosku."
    }
  ];
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setActiveIndex((current) => (current + 1) % items.length);
    }, 8000);

    return () => window.clearInterval(timer);
  }, [items.length]);

  return (
    <div className={`tool-assurance${className ? ` ${className}` : ""}`} aria-label="Informacje o wycenie ubezpieczenia">
      <div className="tool-assurance-track">
        {items.map(({ title, text }, index) => (
          <article className={`tool-assurance-card${activeIndex === index ? " is-active" : ""}`} key={title} aria-hidden={activeIndex !== index}>
            <span>
              <small>{text}</small>
              <strong>{title}</strong>
            </span>
          </article>
        ))}
      </div>
      <div className="tool-assurance-dots" aria-hidden="true">
        {items.map(({ title }, index) => (
          <span className={activeIndex === index ? "is-active" : ""} key={title} />
        ))}
      </div>
    </div>
  );
}

function FieldHeading({
  id,
  label,
  htmlFor,
  help
}: {
  id?: string;
  label: string;
  htmlFor?: string;
  help?: string;
}) {
  return (
    <span className="field-heading">
      {htmlFor ? (
        <label htmlFor={htmlFor}>{label}</label>
      ) : (
        <span className="field-label" id={id}>
          {label}
        </span>
      )}
      <FieldHelp label={label} help={help} />
    </span>
  );
}

function FieldHelp({ label, help }: { label: string; help?: string }) {
  if (!help) return null;

  return (
    <span className="field-help-wrap">
      <button className="field-help-button" type="button" aria-label={`Podpowiedź: ${label}`}>
        <CircleHelp size={15} aria-hidden="true" />
      </button>
      <span className="field-help-popover" role="tooltip">
        {help}
      </span>
    </span>
  );
}
